/**
 * DateRange — 시작일 ~ 종료일 직접 입력.
 *
 * 프리셋 칩(1년·3년·10년)만 두면 "2023년 3월부터"처럼 원하는 구간을 못 고른다.
 * 그래서 날짜 입력이 기본이고, 자주 쓰는 구간은 옆의 빠른 선택으로 채운다.
 *
 * 브라우저 기본 `input[type=date]` 를 쓴다 — 달력 UI·키보드 입력·로케일을
 * 직접 만들면 OS 마다 어긋나고, 접근성도 처음부터 다시 만들어야 한다.
 * 값은 항상 `YYYY-MM-DD` 문자열이다 (input[type=date] 의 value 규격).
 */
import { Button } from "./Button";

/** 오늘로부터 N일 전의 YYYY-MM-DD */
export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
}

/** 로컬 기준 YYYY-MM-DD — toISOString() 은 UTC 로 밀려 하루가 어긋난다 */
export function isoDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export interface DateRangePreset {
  label: string;
  /** 오늘로부터 며칠 전을 시작일로 둘지 */
  days: number;
}

export interface DateRangeProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  /** 빠른 선택 — 없으면 표시하지 않는다 */
  presets?: DateRangePreset[];
  /** 고를 수 있는 가장 이른 날 (예: 데이터 시작일) */
  min?: string;
  /** 고를 수 있는 가장 늦은 날 — 기본값은 오늘(미래를 못 고르게) */
  max?: string;
  disabled?: boolean;
  /** 라벨 대신 읽어줄 설명 */
  label?: string;
}

/**
 * 기간 입력. 시작일과 종료일을 직접 넣고, 옆의 빠른 선택으로 흔한 구간을 채운다.
 *
 * 어디에 쓰였나: 뉴스 트렌드(/trend) 조회 기간
 */
export function DateRange({
  from, to, onChange, presets, min, max, disabled, label = "기간",
}: DateRangeProps) {
  const today = isoDate(new Date());
  const hi = max || today;
  // 시작일이 종료일을 넘지 않게 서로의 경계를 좁힌다 — 뒤집힌 구간은 만들 수 없다
  return (
    <div className="dr" role="group" aria-label={label}>
      <input
        className="dr-input"
        type="date"
        value={from}
        min={min}
        max={to || hi}
        disabled={disabled}
        aria-label={`${label} 시작일`}
        onChange={(e) => onChange({ from: e.target.value, to })}
      />
      <span className="dr-dash" aria-hidden="true">–</span>
      <input
        className="dr-input"
        type="date"
        value={to}
        min={from || min}
        max={hi}
        disabled={disabled}
        aria-label={`${label} 종료일`}
        onChange={(e) => onChange({ from, to: e.target.value })}
      />
      {presets?.length ? (
        <div className="dr-presets">
          {presets.map((p) => (
            <Button
              key={p.label}
              variant="default"
              disabled={disabled}
              onClick={() => onChange({ from: daysAgo(p.days), to: today })}
            >
              {p.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
